import { useEffect, useId, useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { Button } from "../../components/Button";
import { SelectPopover } from "../../components/SelectPopover";
import { cycleTabFocus } from "../../utils/focus-trap";
import { getSafeErrorMessage } from "../../utils/errorHandler";
import {
  PartialImportError,
  importTrustedContactsInChunks,
  parseTrustedContactsCsv,
} from "../../services/trusted-contacts";
import {
  TRUSTED_CONTACT_TYPES,
  type ImportTrustedContactsResult,
  type ParsedTrustedContact,
  type TrustedContactType,
} from "../../types/trusted-contacts";
import { CONTACT_TYPE_LABELS, contactTypeFromLabel, contactTypeLabel } from "./contactTypes";
import "./ImportContactsModal.css";

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired after a successful commit so the directory behind can reload. */
  onImported: (result: ImportTrustedContactsResult) => void;
}

type Stage = "choose" | "reading" | "review" | "importing";

const TYPE_OPTIONS = TRUSTED_CONTACT_TYPES.map((t) => CONTACT_TYPE_LABELS[t]);

/** Matches the server's cap. Checked here too so the coach is told before the upload, not after. */
/**
 * The cap for a spreadsheet or text export, which is what a contacts file usually is.
 *
 * Mirrors `MaxUploadBytes` in `TrustedContactsController`, so a file that would be refused by the
 * server is refused here first with a message about size rather than a failed request.
 */
const MAX_FILE_BYTES = 2 * 1024 * 1024;

/**
 * The cap for a PDF or a photo, which go through the model rather than a parser.
 *
 * Higher than the sheet cap because a single phone photo of a printed page routinely clears 2 MB
 * and refusing it would read as the feature not working.
 *
 * Mirrors `MaxDocumentUploadBytes` in `TrustedContactsController`, and must stay equal to it: a
 * client cap set higher would upload a file the server then refuses, which the coach reads as the
 * upload failing rather than as the file being too big.
 */
const MAX_DOCUMENT_BYTES = 8 * 1024 * 1024;

/** Extensions parsed as rows: a CSV, a text export, or a spreadsheet. */
const SHEET_EXTENSIONS = /\.(csv|txt|xlsx|xls)$/i;

/** Extensions read by the model instead of parsed: a PDF or a photo of a page. */
const DOCUMENT_EXTENSIONS = /\.(pdf|png|jpe?g)$/i;

/** The field names the server maps onto, spelled for a human. */
const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  organization_name: "Organization / team",
  contact_type: "Contact type",
  phone: "Phone",
  email: "Email",
  notes: "Notes",
};

/**
 * Import contacts from a spreadsheet.
 *
 * The server reads the file with the LLM and answers what it found; nothing is written until the
 * coach presses Import here. That is the whole reason there is a review step: a model worked out
 * which column was which, so the coach gets to see that it read "Cell" as the phone and not as
 * something else before 200 rows land in a directory the rest of the staff shares.
 */
export const ImportContactsModal = ({ isOpen, onClose, onImported }: ImportContactsModalProps) => {
  const titleId = useId();
  const typeId = useId();
  const hintId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  /** Read by the Escape handler, which must not close over a stale `stage`. */
  const importingRef = useRef(false);

  const [stage, setStage] = useState<Stage>("choose");
  const [isDragging, setIsDragging] = useState(false);
  /** What a screen reader is told as the stages change; the visual copy is per-stage below. */
  const [statusMessage, setStatusMessage] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ParsedTrustedContact[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Applied to every row the file did not give a type for. */
  const [defaultType, setDefaultType] = useState<TrustedContactType>("CollegeCoach");

  // Reset on open so a second import does not start inside the first one's results.
  useEffect(() => {
    if (!isOpen) return;
    setStage("choose");
    setStatusMessage("");
    setFileName(null);
    setContacts([]);
    setMapping({});
    setWarnings([]);
    setError(null);
    setDefaultType("CollegeCoach");
  }, [isOpen]);

  /*
    Separate effects, not one, and the split is load-bearing.

    `onClose` is an inline arrow at the call site, so it has a new identity on every parent render.
    Folding focus, scroll-lock and the request abort into one effect keyed on it meant an ordinary
    parent re-render — a resolving `load()`, a Firebase token refresh, StrictMode in dev — tore all
    three down and rebuilt them. The abort was the damaging one: it cancelled the in-flight parse,
    the `signal.aborted` guard swallowed the response, and the modal sat on "Reading…" forever with
    no error and no way out. The focus restore also re-captured the wrong element each time.
  */
  useEffect(() => {
    if (!isOpen) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Into the dialog, not left on the trigger behind it — `aria-modal` tells assistive tech the
    // rest of the page is inert, so leaving the cursor out there strands it. The drop zone, not
    // the input: that is now hidden and aria-hidden, and focusing a hidden control is worse than
    // not moving focus at all.
    dropzoneRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      const target = returnFocusRef.current;
      if (target?.isConnected) target.focus();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        // Never mid-commit: batches already sent cannot be taken back, and closing here loses the
        // only report of what landed.
        if (!importingRef.current) onClose();
        return;
      }
      if (e.key === "Tab") cycleTabFocus(dialog, e);
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onClose]);

  // Unmount only. Anything narrower cancels a request the coach is still waiting on.
  useEffect(() => () => requestRef.current?.abort(), []);

  /*
    The zone unmounts while the file is being read and comes back at review, so focus-on-open alone
    leaves a keyboard user on <body> — outside an aria-modal dialog — the moment their file lands.
    Only when it returns, so this never steals focus from the type dropdown or the Import button.
  */
  const wasReading = useRef(false);
  useEffect(() => {
    if (stage === "reading") wasReading.current = true;
    else if (wasReading.current && stage === "review") {
      wasReading.current = false;
      dropzoneRef.current?.focus();
    }
  }, [stage]);

  /*
    A file dropped anywhere OTHER than the zone. The browser's default is to navigate to it, which
    from here means abandoning a review of up to 500 rows because the drop landed an inch wide.

    `globalThis.DragEvent` because React's DragEvent type is imported into this file and shadows
    the DOM one — this handler receives the native event, not the synthetic one.

    Preventing the default alone would leave the whole viewport showing a copy cursor and then
    doing nothing, which is a worse signal than the browser's own "no drop". So a miss inside the
    dialog is read anyway, and a miss outside it says where to aim.
  */
  useEffect(() => {
    if (!isOpen) return;

    const allowDrop = (e: globalThis.DragEvent) => e.preventDefault();

    const handleStrayDrop = (e: globalThis.DragEvent) => {
      e.preventDefault();

      // `window` is a legitimate target here and is not a Node, so it cannot be passed to
      // `contains` — which throws rather than returning false.
      const target = e.target instanceof Node ? e.target : null;
      if (target && dropzoneRef.current?.contains(target)) return;

      const file = e.dataTransfer?.files[0];
      if (!file) return;

      if (target && dialogRef.current?.contains(target)) void readFile(file);
      else setError("Drop the file on the box above.");
    };

    window.addEventListener("dragover", allowDrop);
    window.addEventListener("drop", handleStrayDrop);
    return () => {
      window.removeEventListener("dragover", allowDrop);
      window.removeEventListener("drop", handleStrayDrop);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear immediately so picking the same file twice after an error still fires a change event.
    e.target.value = "";
    if (file) await readFile(file);
  };

  const handleDragOver = (e: DragEvent) => {
    // `dragover` has to preventDefault on EVERY fire or the browser refuses the drop and navigates
    // to the file instead; `dragenter` shares the handler only so the highlight appears at once.
    e.preventDefault();

    // Text and images dragged from another tab are not a file. Highlighting for them promises a
    // drop that would do nothing.
    if (!e.dataTransfer.types.includes("Files")) return;

    // Tells the cursor this is a copy, not a move or a link — otherwise the browser guesses.
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    // Only when the pointer actually leaves the zone. The zone has children, so a bare dragleave
    // fires every time the cursor crosses between them and the highlight strobes.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length === 0) {
      setError("That was not a file. Drop a contacts export, a PDF, or a photo of a page.");
      return;
    }

    // Refused, not silently narrowed to the first. Reading one and announcing the rest cannot
    // work anyway — readFile clears the error as it starts — and picking one of three without
    // saying so is the behaviour worth avoiding.
    if (files.length > 1) {
      setError(`Drop one file at a time — ${files.length} were dropped.`);
      return;
    }

    await readFile(files[0]);
  };

  const readFile = async (file: File) => {
    // Mirrors the extension allowlist in TrustedContactsController.ParseCsv. Checked here as well
    // because `accept` only filters the file dialog — a drop bypasses it entirely.
    const isSheet = SHEET_EXTENSIONS.test(file.name);
    const isDocument = DOCUMENT_EXTENSIONS.test(file.name);

    if (!isSheet && !isDocument) {
      setError(
        `We cannot read ${file.name}. Upload a spreadsheet or CSV, a PDF, or a photo of a page.`
      );
      return;
    }

    // The kind decides the cap, so this is checked second — a 6 MB photo is fine and a 6 MB CSV is
    // not, and telling a coach their photo is too large when it is not would send them off
    // compressing it for nothing.
    const limit = isDocument ? MAX_DOCUMENT_BYTES : MAX_FILE_BYTES;
    if (file.size > limit) {
      setError(
        isDocument
          ? "That file is too large. A photo or PDF of a page is usually well under 8 MB."
          : "That file is too large. Contacts files are usually well under 2 MB."
      );
      return;
    }

    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setFileName(file.name);
    setError(null);
    setStage("reading");
    setStatusMessage(`Reading ${file.name}`);

    try {
      const result = await parseTrustedContactsCsv(file, controller.signal);
      if (controller.signal.aborted) return;

      setContacts(result.contacts);
      setMapping(result.mapping);
      setWarnings(result.warnings);
      setStage("review");
      setStatusMessage(
        result.contacts.length === 0
          ? "No contacts found in that file. Choose a different one."
          : `Found ${result.contacts.length} ${
              result.contacts.length === 1 ? "contact" : "contacts"
            }. Review the columns below.`
      );
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(getSafeErrorMessage(err, "Could not read that file."));
      setStage("choose");
    }
  };

  const handleImport = async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;

    setStage("importing");
    importingRef.current = true;
    setStatusMessage(`Importing ${contacts.length} contacts`);
    setError(null);

    try {
      // The file's own type wins where it had one; the coach's choice fills the rest. This is the
      // point the optional type becomes a required one, which is why ParsedTrustedContact exists.
      const withTypes = contacts.map((c) => ({ ...c, contactType: c.contactType ?? defaultType }));
      const result = await importTrustedContactsInChunks(withTypes, controller.signal);
      if (controller.signal.aborted) return;

      onImported(result);
    } catch (err) {
      if (controller.signal.aborted) return;

      // A failure part-way through a batched commit leaves the earlier batches applied. Saying
      // only "network is down" invites the coach to conclude nothing landed; the import
      // deduplicates, so pressing Import again finishes the job rather than doubling it.
      const landed = err instanceof PartialImportError ? err.result.importedCount : 0;
      const detail = getSafeErrorMessage(err, "Could not import those contacts.");
      setError(
        landed > 0
          ? `${detail} ${landed} of ${contacts.length} were added before it stopped — press Import to finish the rest.`
          : detail
      );
      setStage("review");
    } finally {
      importingRef.current = false;
    }
  };

  const untypedCount = contacts.filter((c) => !c.contactType).length;
  const mappedFields = Object.entries(mapping);

  return (
    <div className="import-contacts-modal-overlay">
      <div
        className="import-contacts-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="import-contacts-modal__header">
          <h2 className="import-contacts-modal__title" id={titleId}>
            Import contacts
          </h2>
          <button
            type="button"
            className="import-contacts-modal__close"
            onClick={onClose}
            disabled={stage === "importing"}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="import-contacts-modal__body">
          {/*
            Both regions stay mounted with their roles in the tree. A live region inserted at the
            same moment as its text is unreliably announced, which is the whole reason they are
            here rather than rendered alongside the content they describe.
          */}
          <div role="alert" className="import-contacts-modal__live-region">
            {error && <div className="import-contacts-modal__error">{error}</div>}
          </div>
          <div
            role="status"
            aria-live="polite"
            className="import-contacts-modal__sr-only"
          >
            {statusMessage}
          </div>

          {stage === "choose" && (
            <p className="import-contacts-modal__lead">
              Upload your contacts as a spreadsheet or CSV, a PDF, or a photo of a page.
              RosterPilot works out which column or line is which, and you will see what it read
              before anything is added.
            </p>
          )}

          {/*
            The picker stays available in review, not just on the way in. Reviewing is exactly when
            a coach discovers the columns were read wrongly, or that a header-only file produced
            nothing — and with no picker here the only way out was Cancel and start again.
          */}
          {(stage === "choose" || stage === "review") && (
            <>
              {/*
                A <button>, not a <div> with a click handler: focusable, Enter/Space-operable and
                announced as a control for free. The `activate` shim the roster uploader uses is
                for markup where a real button is not available — a row, a card — and its own doc
                says to prefer this. The file input is a hidden SIBLING, not a child: an input
                inside a button is invalid HTML and would be a second tab stop.
              */}
              <button
                type="button"
                className={`import-contacts-modal__dropzone${
                  isDragging ? " import-contacts-modal__dropzone--dragging" : ""
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                ref={dropzoneRef}
                aria-describedby={stage === "choose" ? hintId : undefined}
                data-testid="import-contacts-dropzone"
              >
                <svg
                  className="import-contacts-modal__dropzone-icon"
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  {/*
                    An SVG, not "⬆". U+2B06 has Emoji_Presentation=Yes, so Windows, Android and
                    most Chrome installs render it through the emoji font as a blue-and-white tile
                    that ignores `color` entirely.
                  */}
                  <path
                    d="M12 16V4m0 0L7 9m5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="import-contacts-modal__dropzone-lead">
                  {stage === "review"
                    ? "Drop a different file here"
                    : "Drag a file here, or click to choose one"}
                </span>
              </button>
              {/*
                Outside the button, and referenced rather than contained. Inside, it concatenated
                into the button's accessible NAME — 108 characters read out on open and on every
                focus, with no way to skip. As a description it is announced after a pause and can
                be skipped. Only on the way in: by the review stage the coach has plainly managed
                to produce a file we could read.
              */}
              {stage === "choose" && (
                <p className="import-contacts-modal__hint" id={hintId}>
                  Excel, Numbers, CSV, PDF, or a photo of a printed list. Whichever you have.
                </p>
              )}
              {/*
                display:none, not the sr-only class — this input is not for anyone to perceive, and
                sr-only deliberately keeps an element IN the accessibility tree. `.click()` still
                opens the picker while hidden.
              */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,text/csv,application/pdf,image/png,image/jpeg"
                onChange={handleFile}
                className="import-contacts-modal__file-input"
                data-testid="import-contacts-file"
              />
            </>
          )}

          {stage === "reading" && (
            <p className="import-contacts-modal__lead">Reading {fileName}…</p>
          )}

          {(stage === "review" || stage === "importing") && (
            <>
              <p className="import-contacts-modal__lead">
                {contacts.length === 0
                  ? `No contacts were found in ${fileName}. Check it has a header row and a column of names.`
                  : `Found ${contacts.length} ${
                      contacts.length === 1 ? "contact" : "contacts"
                    } in ${fileName}.`}
              </p>

              <div className="import-contacts-modal__field">
                <span className="import-contacts-modal__label" id={`${titleId}-mapping`}>
                  Columns read as
                </span>
                <ul
                  className="import-contacts-modal__mapping"
                  aria-labelledby={`${titleId}-mapping`}
                >
                  {mappedFields.map(([source, target]) => (
                    <li key={source}>
                      <span className="import-contacts-modal__source">{source}</span>
                      {" → "}
                      <strong>{FIELD_LABELS[target] ?? target}</strong>
                    </li>
                  ))}
                </ul>
              </div>

              {warnings.map((warning, index) => (
                <div className="import-contacts-modal__warning" key={`${warning}-${index}`}>
                  {warning}
                </div>
              ))}

              {untypedCount > 0 && (
                <div className="import-contacts-modal__field">
                  <label className="import-contacts-modal__label" htmlFor={typeId}>
                    Add the {untypedCount} {untypedCount === 1 ? "contact" : "contacts"} without a
                    type as
                  </label>
                  <SelectPopover
                    id={typeId}
                    options={TYPE_OPTIONS}
                    value={contactTypeLabel(defaultType)}
                    onChange={(label) => {
                      const type = contactTypeFromLabel(label);
                      if (type) setDefaultType(type);
                    }}
                    placeholder="Choose a type"
                    clearable={false}
                    fullWidth
                  />
                </div>
              )}

              <div className="data-table-wrap import-contacts-modal__preview">
                <table className="data-table" aria-label="Contacts found in the file">
                  <thead>
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Organization</th>
                      <th scope="col">Type</th>
                      <th scope="col">Phone</th>
                      <th scope="col">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact, index) => (
                      <tr key={`${contact.name}-${index}`}>
                        <td>{contact.name}</td>
                        <td className="data-table__muted">{contact.organizationName ?? "—"}</td>
                        <td className="data-table__muted">
                          {contactTypeLabel(contact.contactType ?? defaultType)}
                        </td>
                        <td className="data-table__muted">{contact.phone ?? "—"}</td>
                        <td className="data-table__muted">{contact.email ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/*
                Every row, in a pane that scrolls. Showing a first-25 slice made the review step
                the safety argument leans on cosmetic: a file with 25 clean rows and 475 wrong ones
                would have been committed with the wrong ones never on screen.
              */}
            </>
          )}
        </div>

        <div className="import-contacts-modal__actions">
          {/*
            Disabled mid-commit. Batches already sent cannot be recalled, so closing here would
            leave contacts in the directory with the coach told nothing and the list not reloaded.
          */}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={stage === "importing"}
          >
            Cancel
          </Button>
          {(stage === "review" || stage === "importing") && (
            <Button
              type="button"
              onClick={handleImport}
              loading={stage === "importing"}
              disabled={contacts.length === 0}
            >
              Import {contacts.length}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
